import React from "react";
import PropTypes from "prop-types";
import SplitDateRangeFields from "../../../create-promo/TargetingConditions/SplitDateRangeFields";
import { adjustDateForTimezone } from "../../../create-promo/utils/dateFieldUtils";
import { formatDateRange } from "../../../../utils/dateUtils";
import "./PromoExpiryWindowCell.scss";

/**
 * Stored dates arrive as `YYYY-MM-DD` strings; the pickers want `Date` objects.
 *
 * ⚠ `new Date("2026-08-01")` alone is WRONG here: date-only strings are parsed as UTC midnight, so west
 * of UTC the picker receives the previous local day. That is not merely cosmetic — the untouched end of
 * a window is echoed straight back through `onChange`, so editing one date silently rewrites the other
 * a day earlier, and a start date shifted into the past then fails validation on save.
 *
 * `adjustDateForTimezone` re-anchors to local midnight, which is what the create-promo edit flow already
 * does with the same stored values.
 */
const toDate = (value) => {
  if (!value) return null;
  // ⚠ A Date means it came back from a picker, so it is ALREADY at local midnight. Adjusting a second
  // time subtracts the offset again and lands on the previous day east of UTC — and because the
  // untouched end of a window is echoed back through onChange, a later edit would then persist it.
  // Only the stored "YYYY-MM-DD" strings need re-anchoring.
  if (value instanceof Date) return value;
  return adjustDateForTimezone(new Date(value));
};

/**
 * One editable date window: separate Start Date and End Date pickers.
 *
 * ⚠ Delegates to `SplitDateRangeFields`, the same component the edit-promo flow uses, rather than
 * keeping a second implementation. That component already carries every rule this surface needs — a
 * past start renders locked with a toast explaining why, an editable start cannot be dragged into the
 * past or past its own end, and the end can never fall behind the start or into the past.
 *
 * This replaced a local DateRangePicker/DatePicker pair. The two implementations had already drifted:
 * the range variant cannot freeze one end, so this cell carried a bespoke "locked" branch that rendered
 * the start as static text and offered no explanation when a user tried to click it.
 *
 * `minDate` is passed through for stay dates, which may not begin before the booking window. The rule is
 * also enforced in `validateWindow` on save; having it in the picker as well means the invalid date
 * cannot be chosen in the first place.
 */
const PromoExpiryWindowCell = ({
  loading,
  readOnly,
  startDate,
  endDate,
  minDate,
  highlight,
  onChange,
}) => {
  if (loading) {
    return <span className="promo-expiry-window-skeleton" aria-hidden="true" />;
  }

  const className = ["promo-expiry-window", highlight ? `is-${highlight}` : ""]
    .filter(Boolean)
    .join(" ");

  // Hotel Trader pilot promos: the window is shown but never editable. Rendering static text rather
  // than a disabled picker keeps the row readable and makes it obvious there is nothing to do here.
  if (readOnly) {
    return (
      <div className={className}>
        <span className="promo-expiry-window-static">
          {startDate && endDate
            ? formatDateRange([toDate(startDate), toDate(endDate)])
            : ""}
        </span>
      </div>
    );
  }

  return (
    <div className={className}>
      <SplitDateRangeFields
        startDate={toDate(startDate)}
        endDate={toDate(endDate)}
        minDate={toDate(minDate)}
        // ⚠ These pickers live in a table inside `.rs-modal-body`, which rsuite renders as
        // `overflow: auto` with a viewport-derived max-height. The default inline overlay would be
        // mounted inside that box: the calendar is clipped at the body's edge, and its height is added
        // to the body's scroll area, so the modal gains a tall empty region below the table. Handing
        // the container back to rsuite mounts it on the modal WRAPPER instead — clear of the scroller,
        // and still somewhere rsuite's own z-index rules lift it over the dialog.
        inlinePopup={false}
        // SplitDateRangeFields keeps the DateRangePicker contract and emits a [start, end] pair.
        onChange={([start, end]) =>
          onChange({ startDate: start, endDate: end })
        }
      />
    </div>
  );
};

PromoExpiryWindowCell.propTypes = {
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  highlight: PropTypes.oneOf(["expired", "expiring"]),
  onChange: PropTypes.func.isRequired,
};

export default PromoExpiryWindowCell;
